import { css } from '@emotion/core';
import { useAlert, useRequestState } from '@weave/alert-system';
import {
  ContentLoader,
  DropdownField,
  EmailField,
  FormRow,
  Modal,
  PhoneField,
  SwitchField,
  Text,
  TextField,
  useForm,
} from '@weave/design-system';
import { OrgService } from '@weave/schema-gen-ts/dist/schemas/organization/v1/org.pb';
import { API as IdentityService } from '@weave/schema-gen-ts/dist/apis/api/identity/v1/api.pb';

import { WeaveTheme } from '@weave/theme-original';
import {
  useGetLocationPersonalization,
  useUpsertLocationPersonalization,
} from 'apis/email';
import { useLocationSettingsResource } from 'components/settings/use-location-settings.hook';
import { authorizedFetch } from 'helpers/utils';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Timezones } from '../../../models/timezones';
import { getVerticalList } from '../../../models/vertical-enum';
import {
  LocationModel,
  LocationsActionTypes,
  LocationTypes,
  requestUpdateLocation,
  selectCurrentLocation,
} from '../../../redux/actions/location';

type Props = {
  onClose: () => void;
  orgName: string;
  orgId: string;
  updateOrgName: (value: string) => void;
};

const fieldNames = {
  OrgName: 'OrgName',
  Name: 'Name',
  Email: 'Email',
  Phone: 'Phone',
  Timezone: 'Timezone',
  Type: 'Type',
  VerticalID: 'VerticalID',
  Active: 'Active',
} as const;

export const LocationEdit = ({ onClose, orgName, orgId, updateOrgName }: Props) => {
  const dispatch = useDispatch();
  const alerts = useAlert();
  const location = useSelector(selectCurrentLocation);
  const { loading } = useRequestState(LocationsActionTypes.RequestUpdateLocation);
  const { findSetting, updateSetting } = useLocationSettingsResource('email');

  const emailSettings = findSetting('automated');

  const {
    data: emailPersonalizationData,
    isSuccess: emailPersonalizationDataIsSuccess,
    error: emailPersonalizationDataError,
  } = useGetLocationPersonalization({
    locationId: location.LocationID,
  });

  const { mutate: upsertLocationPersonalization } = useUpsertLocationPersonalization({});

  const { formProps, getFieldProps, isComplete, seedValues, values } = useForm({
    fields: {
      [fieldNames.OrgName]: { type: 'text', required: !!orgId },
      [fieldNames.Name]: { type: 'text', required: true },
      [fieldNames.Email]: { type: 'email' },
      [fieldNames.Phone]: { type: 'phone' },
      [fieldNames.Timezone]: { type: 'dropdown' },
      [fieldNames.Type]: { type: 'dropdown' },
      [fieldNames.VerticalID]: { type: 'dropdown' },
      [fieldNames.Active]: { type: 'switch' },
    },
  });

  useEffect(() => {
    seedValues({
      [fieldNames.OrgName]: orgName ?? '',
      [fieldNames.Name]: location.Name ?? '',
      [fieldNames.Email]: emailSettings?.value ?? '',
      [fieldNames.Phone]: location.Phone ?? '',
      [fieldNames.Timezone]: location.Timezone ?? '',
      [fieldNames.Type]: (location.Type ?? '').toString(),
      [fieldNames.VerticalID]: location.VerticalID ? `${location.VerticalID}` : '',
      [fieldNames.Active]: location.Active ?? false,
    });
  }, [location, emailSettings, orgName]);

  const updateOrganizationName = async (orgId: string, orgName: string) => {
    try {
      await IdentityService.RenameOrg(authorizedFetch({ version: 2 }), {
        orgId,
        newName: orgName,
      });
      updateOrgName(orgName);
      alerts.success('Organization Name Updated Successfully');
    } catch (error) {
      alerts.error('Failed to Update Organization Name');
    }
  };

  const handleSubmit = async () => {
    if (isComplete) {
      const fieldValues = {
        ...values,
        [fieldNames.VerticalID]: Number(values[fieldNames.VerticalID]),
      } as Partial<LocationModel>;

      if (
        emailPersonalizationDataIsSuccess ||
        (typeof emailPersonalizationDataError?.message === 'string' &&
          emailPersonalizationDataError.message.includes('no location personalization'))
      ) {
        const updatedLocalizationPersonalization = {
          from: {
            ...emailPersonalizationData?.locationPersonalization?.from,
            email: emailPersonalizationData?.locationPersonalization?.from?.email ?? '',
          },
          replyTo: {
            ...emailPersonalizationData?.locationPersonalization?.replyTo,
            email: fieldValues.Email ?? '',
          },
        };

        upsertLocationPersonalization({
          locationId: location.LocationID,
          orgId,
          locationPersonalization: updatedLocalizationPersonalization,
        });
      }

      // Check if organization name has changed
      if (values[fieldNames.OrgName] && values[fieldNames.OrgName] !== orgName) {
        await updateOrganizationName(orgId, values[fieldNames.OrgName] ?? '');
      }

      updateSetting({
        set: 'email',
        key: 'automated',
        value: fieldValues.Email || location.Email,
      });

      dispatch(requestUpdateLocation(location.LocationID, fieldValues, onClose));
    }
  };

  return (
    <>
      <ContentLoader show={loading} />
      <Modal.Header>Edit Location</Modal.Header>
      <Modal.Body>
        <form
          {...formProps}
          css={(theme: WeaveTheme) =>
            css`
              margin-top: ${theme.spacing(2)};
            `
          }
        >
          {orgId && (
            <FormRow>
              <TextField
                {...getFieldProps(fieldNames.OrgName)}
                label="Organization Name"
                helperText="Name of the Organization"
              />
            </FormRow>
          )}

          <FormRow>
            <TextField
              {...getFieldProps(fieldNames.Name)}
              label="Location Name"
              helperText="This field will update WAM Location Name and Phone Data Tenant Name"
            />
          </FormRow>
          <FormRow>
            <EmailField {...getFieldProps(fieldNames.Email)} label="Office Email" />
          </FormRow>
          <FormRow>
            <PhoneField
              {...getFieldProps(fieldNames.Phone)}
              label="Outbound Caller Number"
              helperText="This field will update WAM Phone Number and Phone Data Caller Number"
            />
          </FormRow>
          <FormRow>
            <DropdownField
              {...getFieldProps(fieldNames.Timezone)}
              label="Timezone"
              helperText="This field will update WAM Timezone and Phone Data Timezone"
            >
              {Timezones.map((timezone) => (
                <DropdownField.Option
                  key={timezone.ID}
                  value={timezone.Value}
                  searchValue={timezone.Value}
                >
                  {timezone.Value}
                </DropdownField.Option>
              ))}
            </DropdownField>
          </FormRow>
          <FormRow>
            <DropdownField {...getFieldProps(fieldNames.Type)} label="Type">
              <DropdownField.Option value="Regular">Regular</DropdownField.Option>
              <DropdownField.Option value="Demo">Demo</DropdownField.Option>
              <DropdownField.Option value="Dev">Dev</DropdownField.Option>
              <DropdownField.Option value={LocationTypes.External.toString()}>
                External
              </DropdownField.Option>
            </DropdownField>
          </FormRow>
          <FormRow>
            <DropdownField {...getFieldProps(fieldNames.VerticalID)} label="Vertical">
              {getVerticalList().map((vertical) => (
                <DropdownField.Option
                  key={vertical.key}
                  value={`${vertical.key}`}
                  searchValue={vertical.value}
                >
                  {vertical.value}
                </DropdownField.Option>
              ))}
            </DropdownField>
          </FormRow>
          <FormRow>
            <SwitchField {...getFieldProps(fieldNames.Active)} label="Active Status" />
          </FormRow>
          {!values.Active && (
            <FormRow>
              <Text color="error">
                <Text as="span" weight="bold">
                  WARNING:
                </Text>{' '}
                Marking this office inactive will prevent all users from using this
                location. Please be sure this effect is desirable before saving.
              </Text>
            </FormRow>
          )}
        </form>
      </Modal.Body>
      <Modal.Actions
        onSecondaryClick={onClose}
        onPrimaryClick={handleSubmit}
        disablePrimary={!isComplete}
      />
    </>
  );
};
